import React, { useEffect, useState } from 'react';
import { CloseOutlined, Notes as NotesIcon } from '@mui/icons-material';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { gql, useQuery } from '@apollo/client';
import { ServerHighlightGeom, Snippet } from './Interface';
import CitationHighlight from './CitationHighlight';
import { sanitizeNote } from 'op-pages/RO/Notes/sanitizeNote';
import { GET_NOTE } from '../graphql';
import dayjs from 'dayjs';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import EditLetterPanel from './EditLetterPanel';

type CitationPanelVariant = 'side' | 'overlay';
type CitationPanelStyle = {
  isOverlay: boolean;
  panelBorderLeft: string;
  panelHeight: string;
  panelMaxHeight: string;
  panelWidth?: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  contentHeaderBackgroundColor: string;
  contentHeaderTextColor: string;
};

const citationPanelStyles: Record<CitationPanelVariant, CitationPanelStyle> = {
  side: {
    isOverlay: false,
    panelBorderLeft: '1px solid #e0e0e0',
    panelHeight: '100vh',
    panelMaxHeight: '100vh',
    panelWidth: undefined,
    headerBackgroundColor: 'background.paper',
    headerTextColor: 'text.primary',
    contentHeaderBackgroundColor: 'background.secondary',
    contentHeaderTextColor: 'text.primary',
  },
  overlay: {
    isOverlay: true,
    panelBorderLeft: 'none',
    panelHeight: '100%',
    panelMaxHeight: '100%',
    panelWidth: '100%',
    headerBackgroundColor: 'grey.900',
    headerTextColor: 'primary.contrastText',
    contentHeaderBackgroundColor: 'grey.900',
    contentHeaderTextColor: 'primary.contrastText',
  },
};

export interface CitationProps {
  id?: string;
  icon?: React.ReactNode;
  documentType?:
    | 'pdf'
    | 'image'
    | 'Note'
    | 'DocumentPart'
    | 'text'
    | 'letter'
    | 'Appointment'
    | 'LabResultsPanel'
    | 'Diagnosis'
    | 'ROPrescription'
    | 'ROTreatment'
    | string; // Allow any string to handle document type categories like RADIOLOGY_CT, etc.
  documentUrl?: string;
  notes?: string;
  title?: string;
  status?: string;
  dateOfDocument?: string;
  snippets?: Snippet[];
  onClose?: () => void;
  citedText?: string;
  highlightGeoms?: ServerHighlightGeom[];
  author?: string;
  variant?: CitationPanelVariant;
  // Letter-specific props for controlled mode
  letterContent?: string;
  onLetterChange?: (content: string) => void;
  onLetterSave?: () => Promise<number | null>;
  letterSaving?: boolean;
  letterDirty?: boolean;
  patientId?: number;
  conversationId?: number;
  aiLetterId?: number;
  onSendToFhir?: () => void;
  emrDocumentId?: string;
  /** ISO string of when the letter was last saved */
  updatedAt?: string;
  /** Whether the editor is disabled (e.g., during AI generation/editing) */
  isEditorDisabled?: boolean;
}

const GET_SUMMARY_DOCUMENT = gql`
  query GetSummaryDocument($resourceType: String!, $resourceId: String!) {
    getSummaryDocument(resourceType: $resourceType, resourceId: $resourceId)
  }
`;

const CitationPDForImageContent = ({
  documentUrl,
  title,
  status,
  dateOfDocument,
  icon,
  highlightGeoms,
  citedText,
  documentType,
  snippets,
  style,
}: CitationProps & { style: CitationPanelStyle }): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [content, setContent] = useState<string>('');
  const {
    data,
    loading: loadingData,
    error,
  } = useQuery(GET_SUMMARY_DOCUMENT, {
    variables: { resourceType: documentType, resourceId: documentUrl },
    skip: !documentUrl || documentUrl.startsWith('http'),
  });

  const isTextFile = documentType === 'text';

  useEffect(() => {
    const fetchContent = async () => {
      if (!documentUrl) return;

      setLoading(true);
      setFetchError(false);

      try {
        const url = !documentUrl.startsWith('http') ? data?.getSummaryDocument : documentUrl;
        if (!url) return;

        // For text files, fetch and display content directly
        if (isTextFile) {
          const response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            headers: {
              Accept: 'text/plain,text/*,*/*',
              Origin: window.location.origin,
            },
            credentials: 'omit', // Don't send cookies for cross-origin requests
          });
          if (!response.ok) throw new Error('Failed to fetch content');
          const text = await response.text();
          setContent(text);
        }
      } catch (err) {
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    if (!loadingData && (documentUrl?.startsWith('http') || data?.getSummaryDocument)) {
      fetchContent();
    }
  }, [documentUrl, data, loadingData, isTextFile]);

  if (!documentUrl) {
    const snippetText = snippets?.map((s) => s.text).join('\n\n') || '';
    if (!snippetText) {
      return <Typography variant="body2">No document URL provided</Typography>;
    }
    return (
      <Stack height="100%">
        <Stack
          direction="row"
          gap={1}
          sx={{
            padding: '12px 24px',
            backgroundColor: style.contentHeaderBackgroundColor,
            color: style.contentHeaderTextColor,
            height: '64px',
            alignItems: 'center',
            flexShrink: 0,
          }}>
          <Stack direction="column" alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }} color="inherit">
              {title}
            </Typography>
            {dateOfDocument && (
              <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }} color="inherit">
                Date: {dateOfDocument}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Box sx={{ overflow: 'auto', p: 3, fontSize: '16px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          <div dangerouslySetInnerHTML={sanitizeNote(snippetText, true)} />
        </Box>
      </Stack>
    );
  }

  const url = !documentUrl.startsWith('http') ? data?.getSummaryDocument : documentUrl;

  if (!isTextFile && !(loading || loadingData) && !fetchError) {
    return (
      <CitationHighlight
        url={url}
        title={title}
        status={status}
        highlightsGeoJson={highlightGeoms}
        dateOfDocument={dateOfDocument}
        icon={icon}
        documentType={documentType}
        isOverlay={style.isOverlay}
      />
    );
  }
  return (
    <Stack height="100%">
      <Stack
        direction="row"
        gap={1}
        sx={{
          padding: '12px 24px',
          // backgroundColor: 'background.secondary',
          backgroundColor: style.contentHeaderBackgroundColor,
          color: style.contentHeaderTextColor,
          height: '64px',
          alignItems: 'center',
          flexShrink: 0,
        }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
          {icon && (
            <Box
              aria-hidden="true"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 35,
                minHeight: 35,
                flexShrink: 0,
                color: 'inherit',
                '& .MuiSvgIcon-root': {
                  fontSize: '35px',
                  color: 'inherit',
                },
              }}>
              {icon}
            </Box>
          )}
          <Stack direction="column" alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }} color="inherit">
              {title}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }} color="inherit">
              {status && `${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`}
              {status && dateOfDocument && ' | '}
              {dateOfDocument && `Date: ${dateOfDocument}`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {loading || loadingData ? (
        <Skeleton animation="wave" height="100%" />
      ) : fetchError ? (
        <Typography variant="body2" align="center" color="inherit">
          Error loading document
        </Typography>
      ) : isTextFile ? (
        <Box
          sx={{
            position: 'relative',
            height: 'calc(100% - 64px)', // Subtract header height
            width: '100%',
            backgroundColor: 'white',
          }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: 'auto',
              p: 3,
              fontSize: '16px',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              color: 'text.primary',
            }}>
            <div
              dangerouslySetInnerHTML={sanitizeNote(
                citedText
                  ? content.replace(
                      citedText,
                      `<span style="background-color: rgba(251, 188, 5, 0.25);">${citedText}</span>`,
                    )
                  : content,
                true,
              )}
            />
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </Stack>
  );
};

const CitationNotesContent = ({
  documentUrl,
  status,
  icon,
  style,
}: CitationProps & { style: CitationPanelStyle }): JSX.Element => {
  const { data, loading, error } = useQuery(GET_NOTE, {
    variables: { noteId: documentUrl },
    skip: !documentUrl || documentUrl.startsWith('https'),
  });
  const notes = data?.note;

  if (loading) {
    return <Skeleton animation="wave" height="100%" />;
  }

  const formattedMessage = error ? 'Error loading note' : notes?.formattedMessage || '';
  const author = notes?.editedBy?.name || notes?.createdBy?.name || notes?.approvedBy?.name;
  const noteStatus = notes?.status;
  const approvedDate = notes?.approvedAt
    ? dayjs(notes.approvedAt).format(CurrentAppConfig.PatientCard.defaultAlternateDateFormat)
    : null;

  return (
    <Stack height="100%">
      <Stack
        direction="row"
        gap={1}
        sx={{
          padding: '12px 24px',
          backgroundColor: style.contentHeaderBackgroundColor,
          height: '64px',
          alignItems: 'center',
          flexShrink: 0,
        }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
          <Box
            aria-hidden="true"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 35,
              minHeight: 35,
              flexShrink: 0,
              '& .MuiSvgIcon-root': {
                fontSize: '35px',
                color: 'primary.main',
              },
            }}>
            <NotesIcon />
          </Box>
          <Stack direction="column" alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Notes
            </Typography>
            <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              {noteStatus && `${noteStatus.charAt(0).toUpperCase() + noteStatus.slice(1).toLowerCase()} `}
              {author && `(${author}) `}
              {approvedDate && `Date: ${approvedDate}`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Box
        sx={{
          height: 'calc(100% - 64px)', // Subtract header height
          overflow: 'auto',
          padding: '24px 32px 24px 32px',
        }}>
        <Typography variant="body1">
          <div dangerouslySetInnerHTML={sanitizeNote(formattedMessage, true)} />
        </Typography>
      </Box>
    </Stack>
  );
};

const CitationContent = (props: CitationProps & { style: CitationPanelStyle; onClose?: () => void }): JSX.Element => {
  const { style, onClose, ...rest } = props;
  if (
    props.documentType === 'pdf' ||
    props.documentType === 'image' ||
    props.documentType === 'DocumentPart' ||
    props.documentType === 'text'
  ) {
    return <CitationPDForImageContent {...rest} style={style} />;
  } else if (props.documentType === 'Note') {
    return <CitationNotesContent {...rest} style={style} />;
  } else if (props.documentType === 'letter') {
    return <EditLetterPanel {...rest} onClose={onClose} />;
  }
  return <></>;
};

const CitationPanel = ({
  onClose,
  citation,
  headerContent,
  variant = 'side',
}: {
  onClose: () => void;
  citation: CitationProps;
  headerContent?: React.ReactNode;
  variant?: CitationPanelVariant;
}): JSX.Element => {
  const style = citationPanelStyles[variant];
  const isLetterCanvas = citation?.documentType === 'letter';

  return (
    <Stack
      sx={{
        borderLeft: style.panelBorderLeft,
        height: style.panelHeight, // Use viewport height to ensure proper height chain
        maxHeight: style.panelMaxHeight,
        width: style.panelWidth,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {!isLetterCanvas && (
        <Box
          sx={{
            backgroundColor: style.headerBackgroundColor,
            color: style.headerTextColor,
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingX: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {headerContent || (
              <Typography variant="h6" fontWeight={500} color="inherit">
                Citations
              </Typography>
            )}
          </Stack>
          <IconButton onClick={onClose} size="small" aria-label="Close citations panel" color="inherit">
            <CloseOutlined />
          </IconButton>
        </Box>
      )}
      <CitationContent {...citation} onClose={onClose} style={style} variant={variant} />
    </Stack>
  );
};

export default CitationPanel;
